/**
 * Track Merge Store - Pinia
 *
 * Manages state for the track merging feature:
 * - Selected tracks for merging (configurable limit from settings)
 * - Track order (for merge sequence)
 * - Preview mode state
 * - Active track info display
 */

import { defineStore } from 'pinia';
import { appDebugLog } from '@/utils/appDebugLog';
import { MERGE_MODES, validateTracksForMerge, saveMergedTrack, getMergePreviewStats } from '@/utils/TrackMergeService';
import { useMapStore } from '@/stores/map';
import { useSettingsStore } from '@/stores/settings';

export const useTrackMergeStore = defineStore('trackMerge', {
  state: () => ({
    /**
     * Selected tracks for merging, in merge order
     * @type {Array<Object>}
     */
    selectedTracks: [],

    /**
     * Whether the merge drawer is open
     */
    drawerOpen: false,

    /**
     * Whether preview mode is active (showing tracks on map)
     */
    previewActive: false,

    /**
     * Index of the currently displayed track info (0-based)
     */
    activeInfoIndex: 0,

    /**
     * Whether a merge operation is in progress
     */
    merging: false,

    /**
     * Selected merge mode: 'connect', 'multiline', or 'collect'
     * @type {string}
     */
    mergeMode: MERGE_MODES.COLLECT,

    /**
     * Whether to show connector lines (only for 'collect' mode)
     */
    showConnectors: false
  }),

  getters: {
    /**
     * Number of selected tracks
     */
    selectedCount: (state) => state.selectedTracks.length,

    /**
     * Whether we have enough tracks to merge (min 2)
     */
    canMerge: (state) => state.selectedTracks.length >= 2,

    /**
     * Whether we can select more tracks (respects settings limit)
     */
    canSelectMore: (state) => {
      const settingsStore = useSettingsStore();
      return state.selectedTracks.length < settingsStore.maxTracksToMerge;
    },

    /**
     * Get IDs of selected tracks
     */
    selectedTrackIds: (state) => state.selectedTracks.map(t => t.id),

    /**
     * Get the currently active track for info display
     */
    activeTrack: (state) => {
      if (state.selectedTracks.length === 0) return null;
      return state.selectedTracks[state.activeInfoIndex] || state.selectedTracks[0];
    },

    /**
     * Check if a specific track is selected
     */
    isTrackSelected: (state) => (trackId) => {
      return state.selectedTracks.some(t => t.id === trackId);
    },

    /**
     * Get merge preview statistics
     */
    previewStats: (state) => {
      if (state.selectedTracks.length === 0) return null;
      return getMergePreviewStats(state.selectedTracks);
    },

    /**
     * Validation status of current selection
     */
    validationStatus: (state) => {
      if (state.selectedTracks.length === 0) {
        return { valid: false, error: 'No tracks selected' };
      }
      const settingsStore = useSettingsStore();
      return validateTracksForMerge(state.selectedTracks, settingsStore.maxTracksToMerge);
    }
  },

  actions: {
    /**
     * Open the merge drawer
     */
    openDrawer () {
      this.drawerOpen = true;
    },

    /**
     * Close the merge drawer and reset state
     */
    closeDrawer () {
      this.drawerOpen = false;
      this.exitPreviewMode();
      this.clearSelection();
    },

    /**
     * Toggle a track's selection status
     * @param {Object} track - Track object to toggle
     * @returns {boolean} Whether the track is now selected
     */
    toggleTrackSelection (track) {
      const settingsStore = useSettingsStore();
      const maxTracks = settingsStore.maxTracksToMerge;
      const existingIndex = this.selectedTracks.findIndex(t => t.id === track.id);

      if (existingIndex !== -1) {
        // Deselect
        this.selectedTracks.splice(existingIndex, 1);

        // Adjust active index if needed
        if (this.activeInfoIndex >= this.selectedTracks.length) {
          this.activeInfoIndex = Math.max(0, this.selectedTracks.length - 1);
        }

        return false;
      }
      else {
        // Select (if under limit)
        if (this.selectedTracks.length < maxTracks) {
          this.selectedTracks.push(track);
          return true;
        }
        appDebugLog.warn('[TrackMerge] Maximum track limit reached:', maxTracks);
        return false;
      }
    },

    /**
     * Add a track to selection
     * @param {Object} track - Track object to add
     * @returns {boolean} Whether the track was added
     */
    selectTrack (track) {
      const settingsStore = useSettingsStore();
      if (this.isTrackSelected(track.id)) return false;
      if (this.selectedTracks.length >= settingsStore.maxTracksToMerge) return false;

      this.selectedTracks.push(track);
      return true;
    },

    /**
     * Remove a track from selection
     * @param {number} trackId - ID of track to remove
     */
    deselectTrack (trackId) {
      const index = this.selectedTracks.findIndex(t => t.id === trackId);
      if (index !== -1) {
        this.selectedTracks.splice(index, 1);

        // Adjust active index if needed
        if (this.activeInfoIndex >= this.selectedTracks.length) {
          this.activeInfoIndex = Math.max(0, this.selectedTracks.length - 1);
        }
      }
    },

    /**
     * Clear all selected tracks
     */
    clearSelection () {
      this.selectedTracks = [];
      this.activeInfoIndex = 0;
    },

    /**
     * Reset merge selection and clear merge preview layers from the map.
     * Keeps merge mode preferences (mergeMode/showConnectors).
     */
    resetMergeSession () {
      const mapStore = useMapStore();
      mapStore.displayMultipleTracks([]);

      this.clearSelection();
      this.previewActive = false;
    },

    /**
     * Reorder tracks (for drag-and-drop)
     * @param {number} fromIndex - Original index
     * @param {number} toIndex - New index
     */
    moveTrack (fromIndex, toIndex) {
      if (fromIndex < 0 || fromIndex >= this.selectedTracks.length) return;
      if (toIndex < 0 || toIndex >= this.selectedTracks.length) return;

      const [track] = this.selectedTracks.splice(fromIndex, 1);
      this.selectedTracks.splice(toIndex, 0, track);

      // Update active index to follow the moved track if it was active
      if (this.activeInfoIndex === fromIndex) {
        this.activeInfoIndex = toIndex;
      }
      else if (fromIndex < this.activeInfoIndex && toIndex >= this.activeInfoIndex) {
        this.activeInfoIndex--;
      }
      else if (fromIndex > this.activeInfoIndex && toIndex <= this.activeInfoIndex) {
        this.activeInfoIndex++;
      }
    },

    /**
     * Set the complete track order (for drag-and-drop with full reorder)
     * @param {Array<Object>} newOrder - Tracks in new order
     */
    setTrackOrder (newOrder) {
      // Validate that the new order contains same tracks
      if (newOrder.length !== this.selectedTracks.length) return;

      const currentIds = new Set(this.selectedTracks.map(t => t.id));
      const newIds = new Set(newOrder.map(t => t.id));

      if (![...currentIds].every(id => newIds.has(id))) return;

      this.selectedTracks = [...newOrder];
    },

    /**
     * Set the active track for info display
     * @param {number} index - Index of track to show info for
     */
    setActiveInfoIndex (index) {
      if (index >= 0 && index < this.selectedTracks.length) {
        this.activeInfoIndex = index;
      }
    },

    /**
     * Cycle to next track info
     */
    nextTrackInfo () {
      if (this.selectedTracks.length === 0) return;
      this.activeInfoIndex = (this.activeInfoIndex + 1) % this.selectedTracks.length;
    },

    /**
     * Cycle to previous track info
     */
    previousTrackInfo () {
      if (this.selectedTracks.length === 0) return;
      this.activeInfoIndex = this.activeInfoIndex === 0
        ? this.selectedTracks.length - 1
        : this.activeInfoIndex - 1;
    },

    /**
     * Enter preview mode (show tracks on map)
     */
    enterPreviewMode () {
      if (this.selectedTracks.length < 2) {
        appDebugLog.warn('[TrackMerge] Need at least 2 tracks for preview');
        return false;
      }

      this.previewActive = true;
      this.drawerOpen = false; // Close drawer when entering preview
      return true;
    },

    /**
     * Exit preview mode
     */
    exitPreviewMode () {
      this.previewActive = false;
    },

    /**
     * Perform the merge operation
     * @param {Object} options - Merge options
     * @param {string} [options.title] - Custom title
     * @param {string} [options.activityType] - Activity type (defaults to "Mixed")
     * @param {number} [options.activityTypeId] - Activity type ID
     * @returns {Promise<{success: boolean, trackId: number|null, error: string|null}>}
     */
    async performMerge (options = {}) {
      if (!this.canMerge) {
        return { success: false, trackId: null, error: 'Not enough tracks selected' };
      }

      this.merging = true;

      try {
        // Include merge mode and connector options from store state
        const mergeOptions = {
          ...options,
          mergeMode: this.mergeMode,
          showConnectors: this.showConnectors
        };

        return await saveMergedTrack(this.selectedTracks, mergeOptions);
      }
      catch (error) {
        return { success: false, trackId: null, error: error.message };
      }
      finally {
        this.merging = false;
      }
    },

    // =========================================================================
    // Merge Mode Actions
    // =========================================================================

    /**
     * Set the merge mode
     * @param {string} mode - 'connect', 'multiline', or 'collect'
     */
    setMergeMode (mode) {
      if (Object.values(MERGE_MODES).includes(mode)) {
        this.mergeMode = mode;
        appDebugLog.info('[TrackMerge] Merge mode set to:', mode);

        // Disable connectors if not in collect mode
        if (mode !== MERGE_MODES.COLLECT) {
          this.showConnectors = false;
        }
      }
    },

    /**
     * Toggle connector lines visibility
     */
    toggleConnectors () {
      // Only allow connectors in collect mode
      if (this.mergeMode === MERGE_MODES.COLLECT) {
        this.showConnectors = !this.showConnectors;
        appDebugLog.info('[TrackMerge] Connectors:', this.showConnectors ? 'enabled' : 'disabled');
      }
    },

    /**
     * Set connector lines visibility
     * @param {boolean} show - Whether to show connectors
     */
    setShowConnectors (show) {
      if (this.mergeMode === MERGE_MODES.COLLECT) {
        this.showConnectors = show;
      }
    }
  }
});
